import React, {useState, useEffect, useMemo} from 'react';
import moment from 'moment';
import FormsyForm from 'epic-diesel-form/src/components/external/FormsyForm';
import Input from 'epic-diesel-form/src/components/form/Input';
import Select from 'epic-diesel-form/src/components/form/Select';
import TaxRecordStatusCodes from '../../../../constants/TaxRecordStatus';
import Message, {getMessage} from '../../../../components/common/Message';
import {Button, buttonTypes, priority} from 'epic-diesel-common';
import {statesFullNames, canadaProvinces, australiaStates} from '../../../../constants/Countries';
import {getLocale} from 'epic-common';
import {TaxRecord} from '../../../../../shared/types';
import {getTaxSchema} from '../../../../data/formsy';

//@ts-ignore
import styles from './TaxRecordProfile.pcss';

const STATE_SELECT_RC = ['US', 'CA', 'AU'];

export interface TaxRecordProfileProps {
    messages: Record<string, string>;
    taxRecord: any;
    onOpen: () => void;
    updateRecord: (payload?) => void;
    validated?: boolean;
    hasRejected?: boolean;
    retakeAvailable?: boolean;
}

const TaxRecordProfile: React.FC<TaxRecordProfileProps> = ({
    messages,
    taxRecord = {},
    onOpen,
    updateRecord,
    validated,
    hasRejected,
    retakeAvailable
}) => {
    const locale = getLocale();
    const [taxRecordProfile, setTaxRecordProfile] = useState<Partial<TaxRecord>>({});
    const taxInfoSchema = useMemo(() => getTaxSchema(getMessage, messages), []);
    const isStateSelected = STATE_SELECT_RC.includes(taxRecord.addressCountryCode);
    const stateList = useMemo(() => {
        if (!isStateSelected) return [];
        switch (taxRecord.addressCountryCode) {
            case 'US':
                return statesFullNames;
            case 'CA':
                return canadaProvinces;
            case 'AU':
                return australiaStates;
            default:
                return [];
        }
    }, [taxRecord.addressCountryCode]);

    // handle on input validation with mask
    const initialSchema = {
        phoneNumber: taxInfoSchema.phoneNumberWithMask,
        addressLine1: taxInfoSchema.addressLine1WithMask,
        addressLine2: taxInfoSchema.addressLine2WithMask,
        addressCountryCode: taxInfoSchema.addressCountryCodeWithMask,
        addressPostCode: taxInfoSchema.addressPostCodeWithMask,
        addressCity: taxInfoSchema.addressCityWithMask
    };

    const [taxFormSchema, setTaxFormSchema] = useState(initialSchema);

    const reset = () => {
        setTaxRecordProfile(taxRecord);
        setTaxFormSchema(initialSchema);
    };

    useEffect(() => {
        setTaxRecordProfile(taxRecord);
        setTaxFormSchema(initialSchema);
    }, [taxRecord]);

    const verifiedMessage =
        getMessage(messages, 'epic.affiliate.settings.tax.profile.desc.verified') || '';

    const privacyPolicyMessage =
        getMessage(messages, 'epic.affiliate.settings.tax.profile.desc.privacy', [
            `https://www.epicgames.com/site/privacypolicy${locale ? `?lang=${locale}` : ''}`
        ]) || '';

    const _desc = `${verifiedMessage} ${privacyPolicyMessage}`;

    const trigger = (name, value) => {
        const newState = {
            ...taxRecordProfile,
            [name]: value
        };
        setTaxRecordProfile(newState);
    };

    const updateTextValidation = name => {
        setTaxFormSchema({...taxFormSchema, [name]: taxInfoSchema[name]});
    };

    const onChange = e => {
        const {name, value} = e.target;
        switch (name) {
            case 'addressLine1':
            case 'addressLine2':
            case 'addressCountryCode':
            case 'addressPostCode':
            case 'phoneNumber':
            case 'addressCity':
                updateTextValidation(name);
                break;
            default:
                break;
        }
        trigger(name, value);
    };

    const onRetake = () => onOpen();

    const onSaveRecord = model => {
        updateRecord(taxRecordProfile);
    };

    const StatusBar = () => {
        const statusStyles = validated ? styles.taxValidated : styles.taxInValidated;
        return (
            <b className={statusStyles}>
                {getMessage(messages, TaxRecordStatusCodes[taxRecordProfile.taxStatus])}
            </b>
        );
    };

    const elapsedYears = moment().diff(taxRecord.dateOfBirth, 'years');

    return (
        <>
            <div key="tax" className={styles.section}>
                <FormsyForm>
                    <div className={styles.title}>
                        <Message code="epic.affiliate.settings.tax_title" />
                        <StatusBar />
                    </div>
                    <div className={styles.desc}>
                        <p dangerouslySetInnerHTML={{__html: _desc}} />
                    </div>

                    <div className={styles.row}>
                        <div className={styles.fullSection}>
                            <Input
                                disabled
                                onChange={null}
                                name={taxInfoSchema.organizationId.identifier}
                                value={taxRecordProfile.identityId}
                                placeholder={`${
                                    !taxRecordProfile.organizationEntity
                                        ? taxInfoSchema.accountId.display.text
                                        : taxInfoSchema.organizationId.display.text
                                }`}
                            />
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div>
                            <Input
                                disabled
                                onChange={null}
                                name={taxInfoSchema.businessType.identifier}
                                value={
                                    !taxRecordProfile.organizationEntity ? 'Individual' : 'Business'
                                }
                                placeholder={taxInfoSchema.businessType.display.text}
                            />
                        </div>
                        <span />
                        <div>
                            <Input
                                disabled
                                onChange={null}
                                name={taxInfoSchema.recordIdentityKey.identifier}
                                value={taxRecordProfile.tin}
                                placeholder={taxInfoSchema.recordIdentityKey.display.text}
                            />
                        </div>
                    </div>
                    <div className={styles.row}>
                        {!taxRecordProfile.organizationEntity && (
                            <>
                                <div>
                                    <Input
                                        disabled
                                        onChange={null}
                                        name={taxInfoSchema.firstName.identifier}
                                        value={taxRecordProfile.firstName}
                                        placeholder={taxInfoSchema.firstName.display.text}
                                    />
                                </div>
                                <span />
                                <div>
                                    <Input
                                        disabled
                                        onChange={null}
                                        name={taxInfoSchema.lastName.identifier}
                                        value={taxRecordProfile.lastName}
                                        placeholder={taxInfoSchema.lastName.display.text}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <Button
                        type={buttonTypes.SUBMIT}
                        onClick={onRetake}
                        disabled={!retakeAvailable || hasRejected}
                        priority={priority.PRIMARY}
                        theme="dark">
                        <Message code="epic.affiliate.common.submit.retake" />
                    </Button>
                </FormsyForm>
            </div>
            {!taxRecordProfile.organizationEntity && elapsedYears < 18 && (
                <div key="guardian" className={styles.section}>
                    <div className={styles.title}>
                        <Message code="epic.affiliate.form.tax.guardian.title" />
                    </div>
                    <FormsyForm submitForm={onSaveRecord}>
                        <div className={styles.row}>
                            <div>
                                <Input
                                    required
                                    disabled={hasRejected}
                                    onChange={onChange}
                                    name={taxInfoSchema.guardianEmail.identifier}
                                    value={taxRecordProfile.guardianEmail}
                                    placeholder={taxInfoSchema.guardianEmail.display.text}
                                    validations={taxInfoSchema.guardianEmail.validations}
                                    validationErrors={
                                        taxInfoSchema.guardianEmail.validationMessages
                                    }
                                />
                            </div>
                            <span />
                            <div>
                                <Input
                                    disabled={hasRejected}
                                    onChange={onChange}
                                    name={taxInfoSchema.guardianPhoneNumber.identifier}
                                    value={taxRecordProfile.guardianPhoneNumber}
                                    placeholder={taxInfoSchema.guardianPhoneNumber.display.text}
                                    validations={taxInfoSchema.guardianPhoneNumber.validations}
                                    validationErrors={
                                        taxInfoSchema.guardianPhoneNumber.validationMessages
                                    }
                                />
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div>
                                <Input
                                    required
                                    disabled={hasRejected}
                                    onChange={onChange}
                                    name={taxInfoSchema.guardianFirstName.identifier}
                                    value={taxRecordProfile.guardianFirstName}
                                    placeholder={taxInfoSchema.guardianFirstName.display.text}
                                    validations={taxInfoSchema.guardianFirstName.validations}
                                    validationErrors={
                                        taxInfoSchema.guardianFirstName.validationMessages
                                    }
                                />
                            </div>
                            <span />
                            <div>
                                <Input
                                    required
                                    disabled={hasRejected}
                                    onChange={onChange}
                                    name={taxInfoSchema.guardianLastName.identifier}
                                    value={taxRecordProfile.guardianLastName}
                                    placeholder={taxInfoSchema.guardianLastName.display.text}
                                    validations={taxInfoSchema.guardianLastName.validations}
                                    validationErrors={
                                        taxInfoSchema.guardianLastName.validationMessages
                                    }
                                />
                            </div>
                        </div>
                        <Button
                            type={buttonTypes.SUBMIT}
                            disabled={hasRejected}
                            priority={priority.PRIMARY}
                            theme="dark">
                            <Message code="epic.affiliate.common.submit.save" />
                        </Button>
                        <Button onClick={reset} priority={priority.SECONDARY} theme="gray">
                            <Message code="epic.affiliate.common.submit.discard" />
                        </Button>
                    </FormsyForm>
                </div>
            )}

            <div key="address" className={styles.section}>
                <FormsyForm>
                    <div className={styles.title}>
                        <Message code="label.affiliate.form.tax.contact" />
                    </div>
                    <div className={styles.row}>
                        <div>
                            <Input
                                disabled={!taxRecordProfile.organizationEntity || hasRejected}
                                onChange={onChange}
                                name={taxInfoSchema.email.identifier}
                                value={taxRecordProfile.email}
                                placeholder={taxInfoSchema.email.display.text}
                            />
                        </div>
                        <span />
                        <div>
                            <Input
                                required
                                disabled={hasRejected}
                                onChange={onChange}
                                name={taxFormSchema.phoneNumber.identifier}
                                value={taxRecordProfile.phoneNumber}
                                placeholder={taxFormSchema.phoneNumber.display.text}
                                validations={taxFormSchema.phoneNumber.validations}
                                validationErrors={taxFormSchema.phoneNumber.validationMessages}
                            />
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div>
                            <Input
                                required
                                disabled={hasRejected}
                                onChange={onChange}
                                name={taxFormSchema.addressLine1.identifier}
                                value={taxRecordProfile.addressLine1}
                                placeholder={taxFormSchema.addressLine1.display.text}
                                validations={taxFormSchema.addressLine1.validations}
                                validationErrors={taxFormSchema.addressLine1.validationMessages}
                            />
                        </div>
                        <span />
                        <div>
                            <Input
                                disabled={hasRejected}
                                onChange={onChange}
                                name={taxFormSchema.addressLine2.identifier}
                                value={taxRecordProfile.addressLine2}
                                placeholder={taxFormSchema.addressLine2.display.text}
                                validations={taxFormSchema.addressLine2.validations}
                                validationErrors={taxFormSchema.addressLine2.validationMessages}
                            />
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div>
                            <Input
                                disabled={hasRejected}
                                onChange={onChange}
                                name={taxFormSchema.addressCity.identifier}
                                value={taxRecordProfile.addressCity}
                                placeholder={taxInfoSchema.addressCity.display.text}
                                validations={taxFormSchema.addressCity.validations}
                                validationErrors={taxFormSchema.addressCity.validationMessages}
                            />
                        </div>
                        <span />
                        <div>
                            {isStateSelected ? (
                                <Select
                                    options={stateList}
                                    disabled={hasRejected}
                                    onChange={trigger}
                                    name={taxInfoSchema.addressState.identifier}
                                    value={taxRecordProfile.addressState || ''}
                                    validationErrors={taxInfoSchema.addressState.validationMessages}
                                />
                            ) : (
                                <Input
                                    disabled={hasRejected}
                                    onChange={onChange}
                                    name={taxInfoSchema.addressState.identifier}
                                    value={taxRecordProfile.addressState}
                                    placeholder={taxInfoSchema.addressState.display.text}
                                    validationErrors={taxInfoSchema.addressState.validationMessages}
                                />
                            )}
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div>
                            <Input
                                disabled={hasRejected}
                                onChange={onChange}
                                name={taxFormSchema.addressPostCode.identifier}
                                value={taxRecordProfile.addressPostCode}
                                placeholder={taxFormSchema.addressPostCode.display.text}
                                validations={taxFormSchema.addressPostCode.validations}
                                validationErrors={taxFormSchema.addressPostCode.validationMessages}
                            />
                        </div>
                        <span />
                        <div>
                            <Input
                                disabled
                                onChange={null}
                                name={taxInfoSchema.addressCountryCode.identifier}
                                value={taxRecordProfile.addressCountryCode}
                                placeholder={taxInfoSchema.addressCountryCode.display.text}
                                validations={taxFormSchema.addressCountryCode.validations}
                                validationErrors={
                                    taxFormSchema.addressCountryCode.validationMessages
                                }
                            />
                        </div>
                    </div>
                    <Button
                        type={buttonTypes.SUBMIT}
                        disabled={hasRejected}
                        onClick={onSaveRecord}
                        priority={priority.PRIMARY}
                        theme="dark">
                        <Message code="epic.affiliate.common.submit.save" />
                    </Button>
                    <Button onClick={reset} priority={priority.SECONDARY} theme="gray">
                        <Message code="epic.affiliate.common.submit.discard" />
                    </Button>
                </FormsyForm>
            </div>
        </>
    );
};

export default TaxRecordProfile;
